import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CancelIcon,
  Refresh as RetryIcon,
  Visibility as PreviewIcon,
  VisibilityOff as HidePreviewIcon,
  Fullscreen as ExpandIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Message } from '@/types/api';
import { MediaFetchReason, mediaStreamUrl } from '@/api/media';
import { useMediaTask, useSwLive } from '@/hooks/useMedia';
import {
  cancelMedia,
  probeMediaFetch,
  requestMedia,
} from '@/utils/mediaFetchQueue';
import {
  blobCeilingBytes,
  createMediaObjectUrl,
  downloadMediaViaBlob,
  fitsInBlob,
} from '@/utils/mediaDownload';
import { formatFileSize } from '@/utils/formatters';
import { mediaDisplayName, mediaKind } from '@/utils/mediaFile';
import { useChatTheme } from '../ChatThemeContext';
import MediaPreviewOverlay, {
  expandedPreviewFrameSx,
  expandedPreviewImageSx,
} from './MediaPreviewOverlay';

/**
 * Delivery for one file: the cache probe, the request, the progress, the
 * preview and the save.
 *
 * Phase 4 of `docs/plans/manuscript-file-delivery.md`. Rendered only inside an
 * EXPANDED file card, which is the deliberate design of the card and not an
 * accident of layout: nothing here fires on render, on scroll or on viewport
 * entry.
 *
 * ⚠ WHY THERE IS NO VIEWPORT AUTO-PREVIEW. §11.7 contemplates auto-fetching
 * items in the viewport below `preview_auto_max_bytes`, with the FIFO queue
 * keeping that inside `download_user_concurrent`. The queue is built and the
 * `auto` lane is wired, but nothing calls it, because of what session 27
 * measured: the fixed overhead of ONE download is ~15 s of limiter pacing
 * (`FRESH_FLOOR`, not bandwidth), there is ONE download slot across the whole
 * worker, and the account doing it holds ~500 manuscript channels and about
 * four joins left ever. Auto-fetching a scroll would spend that account on
 * files nobody asked to see. A click is cheap to make and impossible to make
 * by accident; that is the trade this file takes. Wiring the auto lane later
 * is a one-line change here, not a redesign.
 */

interface MediaDeliveryPanelProps {
  message: Message;
  /**
   * `availability.downloadable` — whether a request could succeed at all. A
   * file ALREADY in the cache is served regardless of this (the governing rule:
   * what is visible must be downloadable, and `/stream` never consults
   * membership), so it gates the request button and nothing else.
   */
  canRequest: boolean;
}

/**
 * The URL a preview element points at.
 *
 * With a live service worker this is the plain `/stream` URL and the worker
 * attaches the bearer — no query string, no token, and the bytes stream with
 * `Range` support intact. Without one, the file has to be pulled into memory
 * and handed over as an object URL, which is why the degraded path is capped.
 */
const usePreviewUrl = (
  messageId: number,
  enabled: boolean,
  swLive: boolean | null,
  fileSize: number | null | undefined
): { url: string | null; failed: boolean; retry: () => void; fail: () => void } => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const needsBlob = enabled && swLive === false;

  useEffect(() => {
    if (!needsBlob || !fitsInBlob(fileSize)) return undefined;
    let revoked = false;
    let created: string | null = null;
    setFailed(false);
    void createMediaObjectUrl(messageId)
      .then((url) => {
        if (revoked) { URL.revokeObjectURL(url); return; }
        created = url;
        setObjectUrl(url);
      })
      .catch(() => { if (!revoked) setFailed(true); });
    return () => {
      revoked = true;
      // An object URL pins its blob for the lifetime of the document; a
      // reviewer scrolling a chat would otherwise accumulate every image.
      if (created) URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [messageId, needsBlob, fileSize, attempt]);

  const retry = () => { setFailed(false); setAttempt((n) => n + 1); };
  // The worker path has no promise to catch: `<img>`/`<iframe>` report a 401
  // (an expired token mid-preview) or a 409 (evicted between the probe and the
  // render) only through their own `error` event.
  const fail = () => setFailed(true);

  if (!enabled || swLive === null) return { url: null, failed, retry, fail };
  if (swLive) return { url: `${mediaStreamUrl(messageId)}${attempt ? `?_=${attempt}` : ''}`, failed, retry, fail };
  return { url: objectUrl, failed, retry, fail };
};

const REASON_KEYS: Record<MediaFetchReason, string> = {
  not_requested: 'chatPreview.media.delivery.reason.notRequested',
  canceled: 'chatPreview.media.delivery.reason.canceled',
  expired: 'chatPreview.media.delivery.reason.expired',
  unreachable: 'chatPreview.media.delivery.reason.unreachable',
  flood_wait: 'chatPreview.media.delivery.reason.floodWait',
  cache_full: 'chatPreview.media.delivery.reason.cacheFull',
  too_large: 'chatPreview.media.delivery.reason.tooLarge',
  gone: 'chatPreview.media.delivery.reason.gone',
  unknown: 'chatPreview.media.delivery.reason.unknown',
};

/** Reasons a second attempt could plausibly fix. The rest offer no retry. */
const RETRYABLE: ReadonlySet<MediaFetchReason> = new Set<MediaFetchReason>([
  'not_requested', 'canceled', 'expired', 'flood_wait', 'cache_full', 'unknown',
]);

const MediaDeliveryPanel: React.FC<MediaDeliveryPanelProps> = ({ message, canRequest }) => {
  const { colors } = useChatTheme();
  const { t } = useTranslation('pages');
  const task = useMediaTask(message.id);
  const swLive = useSwLive();
  const [showPreview, setShowPreview] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savingBlob, setSavingBlob] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // The preview DOM node, so the overlay can let it hold focus, and the expand
  // control, so focus comes back to it on close.
  const previewElRef = useRef<HTMLElement | null>(null);
  const expandRef = useRef<HTMLButtonElement | null>(null);

  const kind = mediaKind(message.media?.mime_type);
  const fileSize = message.media?.file_size ?? null;
  const displayName = mediaDisplayName(message.media, message.message_id);
  const isReady = task.state === 'ready';

  // One cheap status GET when the card opens: it answers "is it already on
  // disk?" from an `os.path.exists`, enqueues nothing and spends no account.
  useEffect(() => {
    if (task.state === 'unknown') void probeMediaFetch(message.id);
  }, [message.id, task.state]);

  const preview = usePreviewUrl(
    message.id,
    isReady && showPreview && (kind === 'image' || kind === 'pdf'),
    swLive,
    fileSize
  );

  // Expanding is a CSS state on the element the preview hook already mounted —
  // never a portal, never a second element. `MediaPreviewOverlay` carries the
  // full reasoning; the short version is that reparenting an `<iframe>` makes
  // it re-load, which is the bug this feature exists to avoid.
  const canExpand = showPreview && !preview.failed && !!preview.url
    && (kind === 'image' || kind === 'pdf');
  const collapse = useCallback(() => setExpanded(false), []);

  // The overlay cannot outlive what it is showing: hiding the preview, a failed
  // stream or an eviction leaves a black screen with nothing behind it.
  useEffect(() => { if (!canExpand) setExpanded(false); }, [canExpand]);

  // A stable callback ref: an inline arrow is re-invoked on every render (null,
  // then the node), which is harmless but noisy.
  const attachPreviewEl = useCallback((node: HTMLElement | null) => {
    previewElRef.current = node;
  }, []);

  const caption = (text: string, color?: string) => (
    <Typography
      variant="caption"
      sx={{ color: color ?? colors.bubbleTimestamp, fontSize: '0.65rem', lineHeight: 1.5 }}
    >
      {text}
    </Typography>
  );

  const smallButton = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled = false,
    buttonRef?: React.Ref<HTMLButtonElement>
  ) => (
    <Button
      size="small"
      variant="text"
      ref={buttonRef}
      startIcon={icon}
      disabled={disabled}
      onClick={(event) => { event.stopPropagation(); onClick(); }}
      sx={{
        color: colors.bubbleLink,
        fontSize: '0.65rem',
        minWidth: 0,
        py: 0.1,
        px: 0.5,
        '& .MuiButton-startIcon': { mr: 0.4, ml: 0, '& svg': { fontSize: '0.9rem' } },
      }}
    >
      {label}
    </Button>
  );

  // ---- the save control ---------------------------------------------------
  // Two shapes, and they are not interchangeable. With a worker it is a plain
  // anchor: the browser owns the transfer, shows its own progress bar, and can
  // pause and resume it because `/stream` supports `Range`. Without one the
  // whole file comes into memory first, which is why the ceiling is checked
  // BEFORE the click rather than after it fails.
  const renderSaveControl = () => {
    if (swLive === null) {
      return caption(t('chatPreview.media.delivery.swBooting'));
    }
    if (swLive) {
      return (
        <Button
          size="small"
          variant="text"
          component="a"
          href={mediaStreamUrl(message.id, true)}
          download={displayName ?? `file-${message.message_id}`}
          startIcon={<DownloadIcon />}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          sx={{
            color: colors.bubbleLink,
            fontSize: '0.65rem',
            minWidth: 0,
            py: 0.1,
            px: 0.5,
            '& .MuiButton-startIcon': { mr: 0.4, ml: 0, '& svg': { fontSize: '0.9rem' } },
          }}
        >
          {t('chatPreview.media.delivery.download')}
        </Button>
      );
    }
    if (!fitsInBlob(fileSize)) {
      return caption(
        t('chatPreview.media.delivery.degraded.tooBig',
          { limit: formatFileSize(blobCeilingBytes()) }),
        'warning.main'
      );
    }
    return (
      <>
        {smallButton(
          t('chatPreview.media.delivery.download'),
          savingBlob ? <CircularProgress size={12} /> : <DownloadIcon />,
          () => {
            setSavingBlob(true);
            setSaveFailed(false);
            void downloadMediaViaBlob(message.id, displayName)
              .catch(() => setSaveFailed(true))
              .finally(() => setSavingBlob(false));
          },
          savingBlob
        )}
        {caption(t('chatPreview.media.delivery.degraded.notice'))}
        {saveFailed && caption(t('chatPreview.media.delivery.saveFailed'), 'error.main')}
      </>
    );
  };

  const renderPreview = () => {
    if (kind !== 'image' && kind !== 'pdf') return null;
    return (
      <>
        {smallButton(
          t(showPreview
            ? 'chatPreview.media.delivery.hidePreview'
            : 'chatPreview.media.delivery.preview'),
          showPreview ? <HidePreviewIcon /> : <PreviewIcon />,
          () => setShowPreview((open) => !open)
        )}
        {canExpand && smallButton(
          t('chatPreview.media.delivery.expand'),
          <ExpandIcon />,
          () => setExpanded(true),
          false,
          expandRef
        )}
        <MediaPreviewOverlay
          open={expanded}
          onClose={collapse}
          label={t('chatPreview.media.delivery.expandedLabel',
            { name: displayName ?? `file-${message.message_id}` })}
          closeLabel={t('chatPreview.media.delivery.closeExpanded')}
          previewRef={previewElRef}
          returnFocusRef={expandRef}
        />
        {showPreview && preview.failed && (
          <>
            {caption(t('chatPreview.media.delivery.previewFailed'), 'error.main')}
            {smallButton(t('chatPreview.media.delivery.retry'), <RetryIcon />, preview.retry)}
          </>
        )}
        {showPreview && !preview.failed && !preview.url && (
          <CircularProgress size={12} sx={{ color: colors.bubbleTimestamp }} />
        )}
        {showPreview && preview.url && kind === 'image' && (
          // Same element, same `src`, in the same slot of this fragment,
          // whether expanded or not: only the `sx` changes. Moving it, keying
          // it, or rendering a second one would re-fetch the file.
          <Box
            component="img"
            ref={attachPreviewEl}
            src={preview.url}
            alt={displayName ?? ''}
            onError={preview.fail}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            sx={expanded
              ? expandedPreviewImageSx
              : { maxWidth: '100%', maxHeight: 320, borderRadius: 1, mt: 0.5 }}
          />
        )}
        {showPreview && preview.url && kind === 'pdf' && (
          <>
            {/* The response carries `Content-Security-Policy: default-src
                'none'; sandbox`, which is what keeps a hostile PDF from loading
                anything. Whether a given browser's built-in PDF viewer renders
                under that sandbox is NOT settled here — it is one of the
                browser checks this phase exists to run. The new-tab link is the
                fallback that needs no viewer of ours. */}
            <Box
              component="iframe"
              ref={attachPreviewEl}
              src={preview.url}
              onError={preview.fail}
              title={displayName ?? `file-${message.message_id}`}
              sx={expanded
                ? expandedPreviewFrameSx
                : { width: '100%', height: 420, border: 0, borderRadius: 1, mt: 0.5,
                    bgcolor: 'rgba(255,255,255,0.9)' }}
            />
            <Typography
              component="a"
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              variant="caption"
              sx={{ color: colors.bubbleLink, fontSize: '0.65rem', lineHeight: 1.5 }}
            >
              {t('chatPreview.media.delivery.openInTab')}
            </Typography>
          </>
        )}
      </>
    );
  };

  const renderProgress = () => {
    const { loaded, total } = task;
    const known = loaded != null && total != null && total > 0;
    return (
      <>
        <LinearProgress
          variant={known ? 'determinate' : 'indeterminate'}
          value={known ? Math.min(100, (loaded! / total!) * 100) : undefined}
          sx={{ height: 4, borderRadius: 2, my: 0.4 }}
        />
        {caption(known
          ? t('chatPreview.media.delivery.progress', {
              loaded: formatFileSize(loaded!),
              total: formatFileSize(total!),
            })
          : t('chatPreview.media.delivery.progressUnknown'))}
        {/* Measured 2026-08-22: `progress_json.loaded` moves in exact 8 MiB
            steps and the partial file on disk runs several MB ahead of it. The
            bar is a lower bound, and saying so is cheaper than a reviewer
            deciding the transfer has stalled. */}
        {known && caption(t('chatPreview.media.delivery.progressNote'))}
      </>
    );
  };

  const renderErrorState = () => {
    const error = task.error;
    if (!error) return caption(t('chatPreview.media.delivery.error.generic'), 'error.main');

    if (error.status === 429 && error.reason === 'daily') {
      return caption(
        t('chatPreview.media.delivery.error.rateLimitedDaily', {
          used: error.used ?? 0,
          limit: error.limit ?? 0,
          // Digits only. A localized month name inside RTL prose comes out
          // reordered — the same trap the join-ETA bug hit.
          time: error.resetAt ? format(parseISO(error.resetAt), 'HH:mm') : '',
        }),
        'warning.main'
      );
    }
    if (error.status === 429) {
      return caption(t('chatPreview.media.delivery.error.rateLimitedConcurrent'), 'warning.main');
    }
    if (error.status === 503) {
      return caption(t('chatPreview.media.delivery.error.disabled'), 'warning.main');
    }
    if (error.status === 413) {
      return caption(
        t('chatPreview.media.delivery.error.tooLarge', {
          size: error.fileSize != null ? formatFileSize(error.fileSize) : '',
          max: error.maxBytes != null ? formatFileSize(error.maxBytes) : '',
        }),
        'warning.main'
      );
    }
    return caption(t('chatPreview.media.delivery.error.generic'), 'error.main');
  };

  const requestButton = (labelKey: string) =>
    canRequest
      ? smallButton(t(labelKey), <DownloadIcon />, () => requestMedia(message.id, 'manual'))
      : null;

  const body = () => {
    switch (task.state) {
      case 'unknown':
      case 'probing':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={10} sx={{ color: colors.bubbleTimestamp }} />
            {caption(t('chatPreview.media.delivery.checking'))}
          </Box>
        );

      case 'waiting':
        return (
          <>
            {caption(t('chatPreview.media.delivery.queuedLocal'))}
            {smallButton(t('chatPreview.media.delivery.cancel'), <CancelIcon />,
              () => { void cancelMedia(message.id); })}
          </>
        );

      case 'queued':
      case 'running':
        return (
          <>
            {caption(t(task.state === 'queued'
              ? 'chatPreview.media.delivery.queuedServer'
              : 'chatPreview.media.delivery.running'))}
            {task.joined && caption(t('chatPreview.media.delivery.joined'))}
            {task.state === 'running' && renderProgress()}
            {smallButton(t('chatPreview.media.delivery.cancel'), <CancelIcon />,
              () => { void cancelMedia(message.id); })}
          </>
        );

      case 'ready':
        return (
          <>
            {caption(t('chatPreview.media.delivery.ready'), 'success.main')}
            {renderPreview()}
            {renderSaveControl()}
          </>
        );

      case 'unavailable': {
        const reason = task.reason ?? 'unknown';
        return (
          <>
            {caption(t(REASON_KEYS[reason]),
              reason === 'not_requested' ? undefined : 'warning.main')}
            {RETRYABLE.has(reason) && requestButton(
              reason === 'not_requested'
                ? 'chatPreview.media.delivery.request'
                : 'chatPreview.media.delivery.retry')}
          </>
        );
      }

      case 'error':
      default:
        return (
          <>
            {renderErrorState()}
            {requestButton('chatPreview.media.delivery.retry')}
          </>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
      {body()}
    </Box>
  );
};

export default MediaDeliveryPanel;
